import { useState, useEffect } from 'react'
import { supabase } from '../../lib/supabase'
import { useAuth } from '../../context/AuthContext'
import { useToast } from '../ui/Toast'
import { useConfirm } from '../ui/ConfirmDialog'
import Modal from '../ui/Modal'
import Button from '../ui/Button'
import { MoneyInput } from '../ui/Input'
import { fmt, fmtS, today, uid } from '../../utils/formatters'
import { exportAttendanceToExcel } from '../../utils/exportExcel'
import { SkeletonStats, SkeletonList } from '../ui/Skeleton'

const ROLES = ['Mandor', 'Tukang Batu', 'Tukang Besi', 'Tukang Kayu', 'Tukang Cat', 'Kenek', 'Operator']

export default function Workers() {
  const { user } = useAuth()
  const toast = useToast()
  const confirm = useConfirm()
  const [workers, setWorkers] = useState([])
  const [attendance, setAttendance] = useState([])
  const [projects, setProjects] = useState([])
  const [loading, setLoading] = useState(true)
  const [show, setShow] = useState(false)
  const [edit, setEdit] = useState(null)
  const [filterProject, setFilterProject] = useState('all')
  const [attDate, setAttDate] = useState(today())
  const [tab, setTab] = useState('list')

  const blank = { name: '', role: 'Tukang Batu', project_id: '', daily_wage: 250000, phone: '' }
  const [form, setForm] = useState(blank)

  useEffect(() => {
    if (!user) return
    fetchData()
  }, [user])

  const fetchData = async () => {
    setLoading(true)
    const [{ data: w }, { data: a }, { data: p }] = await Promise.all([
      supabase.from('workers').select('*').eq('user_id', user.id).order('name'),
      supabase.from('attendance').select('*').eq('user_id', user.id),
      supabase.from('projects').select('*').eq('user_id', user.id),
    ])
    if (w) setWorkers(w)
    if (a) setAttendance(a)
    if (p) setProjects(p)
    setLoading(false)
  }

  const save = async () => {
    if (!form.name) { toast.show('Nama pekerja wajib diisi', 'error'); return }
    const data = { ...form, daily_wage: parseInt(form.daily_wage) || 0, user_id: user.id }
    if (edit) {
      await supabase.from('workers').update(data).eq('id', edit)
    } else {
      await supabase.from('workers').insert([{ ...data, id: uid() }])
    }
    setShow(false)
    toast.show(edit ? 'Pekerja diperbarui' : 'Pekerja ditambahkan')
    fetchData()
  }

  const del = async (id, name) => {
    const ok = await confirm(`Hapus pekerja "${name}"? Data absensi terkait juga akan terhapus.`, {
      title: 'Hapus Pekerja', icon: '👷', confirmLabel: 'Hapus', confirmVariant: 'danger'
    })
    if (!ok) return
    await supabase.from('attendance').delete().eq('worker_id', id)
    await supabase.from('workers').delete().eq('id', id)
    toast.show('Pekerja dihapus')
    fetchData()
  }

  const setAtt = async (workerId, status) => {
    const existing = attendance.find(a => a.worker_id === workerId && a.date === attDate)
    if (existing) {
      await supabase.from('attendance').update({ status }).eq('id', existing.id)
    } else {
      await supabase.from('attendance').insert([{ id: uid(), worker_id: workerId, date: attDate, status, user_id: user.id }])
    }
    fetchData()
  }

  const getAtt = (workerId) =>
    attendance.find(a => a.worker_id === workerId && a.date === attDate)?.status || null

  const filteredWorkers = filterProject === 'all'
    ? workers : workers.filter(w => w.project_id === filterProject)

  // Attendance summary for selected date
  const dayAtt = attendance.filter(a => a.date === attDate)
  const presentCount = dayAtt.filter(a => a.status === 'present').length
  const halfCount = dayAtt.filter(a => a.status === 'half').length
  const absentCount = dayAtt.filter(a => a.status === 'absent').length
  const estimatedCost = filteredWorkers.reduce((s, w) => {
    const att = getAtt(w.id)
    if (att === 'present') return s + (w.daily_wage || 0)
    if (att === 'half') return s + (w.daily_wage || 0) / 2
    return s
  }, 0)

  const handleExportAttendance = () => {
    const data = dayAtt.map(a => {
      const w = workers.find(w => w.id === a.worker_id)
      return { ...a, worker: w }
    })
    exportAttendanceToExcel(data, workers)
    toast.show('Excel absensi diunduh')
  }

  if (loading) {
    return <div><SkeletonStats cols={3} /><SkeletonList count={4} /></div>
  }

  return (
    <div>
      {/* Stats */}
      <div className="worker-stats">
        <div className="stat-card">
          <div className="stat-value">{workers.length}</div>
          <div className="stat-label">Total Pekerja</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color: 'var(--accent)' }}>
            {fmtS(workers.reduce((s, w) => s + (w.daily_wage || 0), 0))}
          </div>
          <div className="stat-label">Total Upah/Hari</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color: 'var(--info)' }}>
            {[...new Set(workers.map(w => w.role))].length}
          </div>
          <div className="stat-label">Keahlian</div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tab-bar">
        <div className={`tab-item${tab === 'list' ? ' active' : ''}`} onClick={() => setTab('list')}>👷 Daftar Pekerja</div>
        <div className={`tab-item${tab === 'attendance' ? ' active' : ''}`} onClick={() => setTab('attendance')}>📋 Absensi Harian</div>
      </div>

      {/* Filter + Actions */}
      <div className="filter-row">
        <select className="form-select" style={{ width: 180 }} value={filterProject} onChange={e => setFilterProject(e.target.value)}>
          <option value="all">Semua Proyek</option>
          {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        {tab === 'attendance' && (
          <>
            <input className="form-input" type="date" style={{ width: 150 }} value={attDate} onChange={e => setAttDate(e.target.value)} />
            <Button variant="secondary" onClick={handleExportAttendance}>📊 Export</Button>
          </>
        )}
        <Button onClick={() => { setForm(blank); setEdit(null); setShow(true) }}>➕ Pekerja</Button>
      </div>

      {/* === DAFTAR PEKERJA === */}
      {tab === 'list' && (
        <>
          {filteredWorkers.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">👷</div>
              <p>Belum ada pekerja terdaftar</p>
              <Button variant="primary" style={{ marginTop: 12 }} onClick={() => { setForm(blank); setEdit(null); setShow(true) }}>
                ➕ Tambah Pekerja Pertama
              </Button>
            </div>
          ) : (
            filteredWorkers.map(w => {
              const proj = projects.find(p => p.id === w.project_id)
              return (
                <div key={w.id} className="mobile-card-item">
                  <div className="mc-header">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                      <div className="worker-avatar">{w.name.charAt(0).toUpperCase()}</div>
                      <div>
                        <div className="mc-title">{w.name}</div>
                        <div style={{ fontSize: 10, color: 'var(--accent)', fontWeight: 600 }}>{w.role}</div>
                      </div>
                    </div>
                    <span className="badge badge-accent">{proj?.name || 'Umum'}</span>
                  </div>
                  <div className="mc-row">
                    <span className="mc-label">Upah/Hari</span>
                    <span className="mc-value" style={{ color: 'var(--accent)' }}>{fmt(w.daily_wage)}</span>
                  </div>
                  {w.phone && (
                    <div className="mc-row">
                      <span className="mc-label">Telepon</span>
                      <span className="mc-value">{w.phone}</span>
                    </div>
                  )}
                  <div className="mc-actions">
                    <Button size="sm" variant="secondary" onClick={() => { setForm(w); setEdit(w.id); setShow(true) }}>✏️ Edit</Button>
                    <Button size="sm" variant="danger" onClick={() => del(w.id, w.name)}>🗑️ Hapus</Button>
                  </div>
                </div>
              )
            })
          )}
        </>
      )}

      {/* === ABSENSI === */}
      {tab === 'attendance' && (
        <>
          <div className="att-summary">
            <div className="att-sum-item">
              <div className="att-sum-val" style={{ color: 'var(--success)' }}>{presentCount}</div>
              <div className="att-sum-lbl">Hadir</div>
            </div>
            <div className="att-sum-item">
              <div className="att-sum-val" style={{ color: 'var(--warning)' }}>{halfCount}</div>
              <div className="att-sum-lbl">Setengah</div>
            </div>
            <div className="att-sum-item">
              <div className="att-sum-val" style={{ color: 'var(--danger)' }}>{absentCount}</div>
              <div className="att-sum-lbl">Absen</div>
            </div>
          </div>

          {estimatedCost > 0 && (
            <div style={{
              background: 'var(--accent-bg)', border: '1px solid rgba(245,158,11,.2)',
              borderRadius: 'var(--radius-lg)', padding: '10px 14px',
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              marginBottom: 12
            }}>
              <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>💰 Estimasi biaya hari ini:</span>
              <span style={{ fontWeight: 800, color: 'var(--accent)', fontSize: 14 }}>{fmtS(estimatedCost)}</span>
            </div>
          )}

          <div className="attendance-grid">
            {filteredWorkers.map(w => {
              const att = getAtt(w.id)
              const proj = projects.find(p => p.id === w.project_id)
              return (
                <div key={w.id} className="worker-card">
                  <div className="worker-avatar">{w.name.charAt(0).toUpperCase()}</div>
                  <div className="worker-info">
                    <h4>{w.name}</h4>
                    <p>{w.role}{proj ? ` • ${proj.name}` : ''} • {fmt(w.daily_wage)}/hari</p>
                  </div>
                  <div className="att-buttons">
                    <button className={`att-btn${att === 'present' ? ' present' : ''}`} onClick={() => setAtt(w.id, 'present')} title="Hadir">✓</button>
                    <button className={`att-btn${att === 'half' ? ' half' : ''}`} onClick={() => setAtt(w.id, 'half')} title="Setengah Hari">½</button>
                    <button className={`att-btn${att === 'absent' ? ' absent' : ''}`} onClick={() => setAtt(w.id, 'absent')} title="Absen">✗</button>
                  </div>
                </div>
              )
            })}
          </div>

          {filteredWorkers.length === 0 && (
            <div className="empty-state"><p>Belum ada pekerja untuk proyek ini</p></div>
          )}
        </>
      )}

      {/* Modal Form */}
      <Modal
        show={show}
        onClose={() => setShow(false)}
        title={edit ? '✏️ Edit Pekerja' : '➕ Tambah Pekerja'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setShow(false)}>Batal</Button>
            <Button onClick={save}>💾 Simpan</Button>
          </>
        }
      >
        <div className="form-group">
          <label className="form-label">Nama *</label>
          <input className="form-input" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Nama lengkap" />
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Jabatan</label>
            <select className="form-select" value={form.role} onChange={e => setForm({ ...form, role: e.target.value })}>
              {ROLES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Proyek</label>
            <select className="form-select" value={form.project_id} onChange={e => setForm({ ...form, project_id: e.target.value })}>
              <option value="">Pilih Proyek</option>
              {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
        </div>
        <div className="form-row">
          <div className="form-group">
            <label className="form-label">Upah/Hari</label>
            <MoneyInput value={form.daily_wage} onChange={v => setForm({ ...form, daily_wage: v })} />
          </div>
          <div className="form-group">
            <label className="form-label">No. Telepon</label>
            <input className="form-input" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="08xx-xxxx-xxxx" />
          </div>
        </div>
      </Modal>
    </div>
  )
}